'use client';

// @boty/ui - Button component
// Wraps MUI Button with Boty styling

import type { ReactNode } from 'react';
import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export interface ButtonProps extends Omit<MuiButtonProps, 'children'> {
  children: ReactNode;
  loading?: boolean;
}

/**
 * Button - Styled MUI Button with Boty theme
 *
 * @param children - Button content
 * @param loading - Shows loading spinner and disables button when true
 * @param variant - 'contained' (default), 'outlined', 'text'
 * @param color - 'primary' (default), 'secondary', 'success', 'error', 'warning', 'info'
 * @param size - 'small', 'medium' (default), 'large'
 * @param ...props - All standard MUI Button props
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button onClick={handleClick}>Click me</Button>
 *
 * // Secondary outlined button
 * <Button variant="outlined" color="secondary">
 *   Secondary
 * </Button>
 *
 * // Loading state
 * <Button loading={isSubmitting}>Submit</Button>
 * ```
 */
export function Button({
  children,
  loading = false,
  disabled,
  variant = 'contained',
  ...props
}: ButtonProps): ReactNode {
  return (
    <MuiButton
      variant={variant}
      disabled={disabled ?? loading}
      {...props}
    >
      {loading ? (
        <CircularProgress
          size={20}
          color="inherit"
          sx={{ marginInlineEnd: 1 }}
        />
      ) : null}
      {children}
    </MuiButton>
  );
}

Button.displayName = 'Button';
